import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { AppContext } from "../../App/context";
import Elements from "../Elements";
import { css, keyframes } from "styled-components";
import { pixelToRem } from "../../../utils";
import Icons from "../../../images/icons";

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); box-shadow: 0 0 ${pixelToRem(20)} rgba(37, 211, 102, 0.5); }
  100% { transform: scale(1); }
`;

const WhatsAppButton: React.FC = () => {
  const { appData } = useContext(AppContext);
  const [visible, setVisible] = useState(true);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const lastActionTime = useRef(Date.now());
  const scrollTimeout = useRef<any>(null);
  const isHovering = useRef(false);
  const hideTimeout = useRef<any>(null);
  const tooltipVisibleRef = useRef(false);

  const handleAction = useCallback(() => {
    lastActionTime.current = Date.now();
    setIsInactive(false);

    if (!isHovering.current && tooltipVisibleRef.current) {
      // Si ya hay un temporizador de ocultado en marcha, no lo reiniciamos
      // para que se oculte exactamente 3s después de detectar la primera actividad.
      if (hideTimeout.current) return;

      hideTimeout.current = setTimeout(() => {
        if (!isHovering.current) {
          setTooltipVisible(false);
          tooltipVisibleRef.current = false;
        }
        hideTimeout.current = null;
      }, 3000);
    }
  }, []);

  useEffect(() => {
    // 2. Mobile Scroll Behavior & Inactivity Tracking
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      handleAction();
      const currentScrollY = window.scrollY;

      // Mobile Transparency Trigger
      if (window.innerWidth <= 768) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsScrollingDown(true);
        } else {
          setIsScrollingDown(false);
        }
      }

      lastScrollY = currentScrollY;

      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => {
        setIsScrollingDown(false);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleAction);
    window.addEventListener("keydown", handleAction);

    // 3. Inactivity Timer
    const interval = setInterval(() => {
      const now = Date.now();
      const diff = now - lastActionTime.current;

      // Show tooltip after 8s of inactivity
      if (diff > 8000 && visible && !tooltipVisibleRef.current) {
        setTooltipVisible(true);
        tooltipVisibleRef.current = true;
      }

      // Pulse animation after 45s of inactivity
      if (diff > 45000) {
        setIsInactive(true);
      }
    }, 1000);

    return () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleAction);
      window.removeEventListener("keydown", handleAction);
      clearInterval(interval);
    };
  }, [handleAction]);

  if (!appData?.features?.whatsapp?.enabled) {
    return null;
  }

  const { whatsapp } = appData.features;
  const { base_url, phone, message, message_key } = whatsapp;

  if (!base_url || !phone) {
    return null;
  }

  // Construct URL with parameters
  const url = new URL(base_url);
  url.searchParams.append("phone", phone);
  if (message && message_key) {
    url.searchParams.append(message_key, `"${message}"`);
  }
  url.searchParams.append("type", "phone_number");
  url.searchParams.append("app_absent", "0");

  /*
    // DATOS Y ANALÍTICA (Point 3 suggestion):
    // Sugerencia de implementación para trackear métricas solicitadas:
    
    const trackWhatsAppInteraction = (type) => {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'whatsapp_button_click',
          device: window.innerWidth <= 768 ? 'mobile' : 'desktop',
          interaction_type: type, // 'standard' o 'after_inactivity'
          timestamp: new Date().toISOString()
        });
      }
    };
    
    // Para implementar, llamar a trackWhatsAppInteraction('standard') 
    // o trackWhatsAppInteraction('after_inactivity') en el onClick del Elements.A.
  */

  return (
    <Elements.Wrapper
      customCss={css`
        position: fixed;
        bottom: ${pixelToRem(20)};
        right: ${pixelToRem(20)};
        z-index: 1000;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        transition:
          opacity 0.4s ease,
          transform 0.5s ease;
        opacity: ${visible ? (isScrollingDown ? 0.4 : 1) : 0};
        transform: translateY(${visible ? 0 : pixelToRem(20)});
        pointer-events: ${visible ? "auto" : "none"};

        @media (max-width: ${({ theme }) =>
            pixelToRem(theme.responsive.tablet.maxWidth)}) {
          bottom: 7.5625rem;
          right: ${pixelToRem(15)};
        }
      `}
    >
      {tooltipVisible && (
        <Elements.Wrapper
          customCss={css`
            background-color: white;
            padding: ${pixelToRem(12)} ${pixelToRem(16)};
            border-radius: ${pixelToRem(12)};
            box-shadow: 0 ${pixelToRem(4)} ${pixelToRem(15)} rgba(0, 0, 0, 0.12);
            margin-bottom: ${pixelToRem(12)};
            position: relative;
            max-width: ${pixelToRem(220)};
            animation: tooltipIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

            &::after {
              content: "";
              position: absolute;
              bottom: ${pixelToRem(-8)};
              right: ${pixelToRem(20)};
              border-width: ${pixelToRem(8)} ${pixelToRem(8)} 0;
              border-style: solid;
              border-color: white transparent transparent;
            }

            @keyframes tooltipIn {
              from {
                opacity: 0;
                transform: translateY(10px) scale(0.9);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}
        >
          <Elements.Span
            customCss={css`
              font-size: ${pixelToRem(13)};
              color: #333;
              font-family: ${({ theme }) => theme.font.family.primary.bold};
              line-height: 1.3;
              display: block;
              padding-right: ${pixelToRem(10)};
            `}
          >
            ¿Algún problema con el formulario? Te ayudo aquí
          </Elements.Span>
          <Elements.Button
            onClick={() => {
              setTooltipVisible(false);
              tooltipVisibleRef.current = false;
            }}
            customCss={css`
              position: absolute;
              top: ${pixelToRem(4)};
              right: ${pixelToRem(6)};
              background: none;
              border: none;
              font-size: ${pixelToRem(14)};
              color: #bbb;
              cursor: pointer;
              padding: 0;
              min-height: auto;
              line-height: 1;

              &:hover {
                color: #666;
                background: none;
              }
            `}
          >
            ×
          </Elements.Button>
        </Elements.Wrapper>
      )}
      <Elements.A
        href={url.toString()}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => {
          if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
          }
          isHovering.current = true;
          setTooltipVisible(true);
          tooltipVisibleRef.current = true;
        }}
        onMouseLeave={() => {
          isHovering.current = false;
          if (hideTimeout.current) clearTimeout(hideTimeout.current);
          hideTimeout.current = setTimeout(() => {
            if (!isHovering.current) {
              setTooltipVisible(false);
              tooltipVisibleRef.current = false;
            }
          }, 3000);
        }}
        customCss={css`
          width: ${pixelToRem(60)};
          height: ${pixelToRem(60)};
          background-color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 ${pixelToRem(4)} ${pixelToRem(15)} rgba(0, 0, 0, 0.15);
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
          text-decoration: none;
          animation: ${isInactive ? pulse : "none"} 2.5s infinite ease-in-out;

          &:hover {
            transform: scale(1.05);
            box-shadow: 0 ${pixelToRem(6)} ${pixelToRem(20)} rgba(0, 0, 0, 0.2);
          }

          @media (max-width: ${({ theme }) =>
              pixelToRem(theme.responsive.tablet.maxWidth)}) {
            width: ${pixelToRem(56)};
            height: ${pixelToRem(56)};
          }
        `}
      >
        <Elements.Img
          src={Icons.WSIcon}
          alt="WhatsApp"
          customCss={css`
            width: ${pixelToRem(36)};
            height: auto;

            @media (max-width: ${({ theme }) =>
                pixelToRem(theme.responsive.tablet.maxWidth)}) {
              width: 65%;
            }
          `}
        />
      </Elements.A>
    </Elements.Wrapper>
  );
};

export default WhatsAppButton;
